import React, { useState, useEffect } from 'react';
import { Clock, ArrowRight, Crown, Sparkles, AlertCircle, RefreshCw, Shield, Coins } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { MembershipInfo, UserLevel } from '../types';
import {
  getMembershipInfo,
  formatMinutes,
  formatMinutesEn,
  getUserLevelName,
} from '../services/membership';

interface MembershipStatusProps {
  onUpgrade: () => void;
  onViewPlan?: () => void;
}

export default function MembershipStatus({ onUpgrade, onViewPlan }: MembershipStatusProps) {
  const { t, language } = useLanguage();
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    loadMembershipInfo();
    // 每30秒自动刷新会员信息（扣费后及时更新）
    const timer = setInterval(() => {
      loadMembershipInfo();
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const loadMembershipInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await getMembershipInfo();
      setMembershipInfo(info);
    } catch (err: any) {
      setError(err.message || '加载会员信息失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="relative rounded-pill bg-surface border border-surface-border p-4">
        <div className="flex items-center justify-center py-4">
          <RefreshCw size={20} className="animate-spin text-ink-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative rounded-pill bg-surface border border-surface-border p-4">
        <div className="flex items-center gap-2 text-red-500 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
        <button
          onClick={loadMembershipInfo}
          className="mt-2 text-xs text-ink-secondary hover:text-brand-900 underline"
        >
          {language === 'zh' ? '重试' : 'Retry'}
        </button>
      </div>
    );
  }

  if (!membershipInfo) {
    return null;
  }

  const { userLevel, freeQuotaMinutes, freeQuotaTotal, membershipQuotaMinutes, membershipQuotaTotal, purchasedMinutes, membershipMonths, lastQuotaRefreshAt } =
    membershipInfo;

  const isCofounder = membershipInfo.isCofounder === true;
  const isPro = userLevel === 'pro';
  const isTeam = userLevel === 'team';
  const isFree = userLevel === 'free';
  const isPaid = isPro || isTeam;

  // 使用后端返回的到期时间和剩余天数
  const daysRemaining = membershipInfo.daysUntilExpire ?? 0;
  const expireAtStr = membershipInfo.membershipExpireAt;
  const formatExpiry = (dateStr: string) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return language === 'zh' ? `${y}年${m}月${d}日` : `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  // 月度配额 = 免费 + 会员
  const monthlyRemaining = freeQuotaMinutes + membershipQuotaMinutes;
  const monthlyCap = freeQuotaTotal + (membershipQuotaTotal > 0 ? membershipQuotaTotal : 0);
  // 进度条百分比（基于月度配额）
  const usagePercent = isTeam ? 100 : (monthlyCap > 0 ? Math.min(100, Math.round((monthlyRemaining / monthlyCap) * 100)) : 0);

  // 格式化时长显示
  const formatQuota = (minutes: number) => {
    return language === 'zh' ? formatMinutes(minutes) : formatMinutesEn(minutes);
  };

  return (
    <div
      className={`relative rounded-pill border ${
        isPaid
          ? 'bg-gradient-to-br from-white to-prestige-100 border-prestige-border shadow-prestige ring-1 ring-prestige-border/30'
          : 'bg-surface border-surface-border'
      }`}
    >
      {isPaid && (
        <>
          <div className="absolute top-[-30px] right-[-30px] w-40 h-40 bg-prestige-200/40 blur-[40px] rounded-full"></div>
          <div className="absolute inset-0 bg-noise opacity-[0.04] pointer-events-none"></div>
        </>
      )}

      <div className="p-4 relative z-10">
        {/* 头部：等级标识 */}
        <div className="flex justify-between items-center mb-3">
          <div className="font-serif font-black text-lg tracking-tight flex items-center gap-1">
            {isTeam ? (
              <span className="text-prestige-300 drop-shadow-sm flex items-center gap-1.5 relative cursor-default"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <Crown size={16} fill="currentColor" />
                Team <span className="italic font-bold ml-0.5 whitespace-nowrap opacity-80">Plan</span>
                {showTooltip && (
                  <div className="absolute left-0 bottom-full mb-2 z-[9999]">
                    <div className="bg-prestige-900 text-white text-[11px] rounded-lg px-3 py-2.5 shadow-xl whitespace-nowrap space-y-1">
                      {expireAtStr && (
                        <div>{language === 'zh'
                          ? `到期：${formatExpiry(expireAtStr)}（剩余 ${daysRemaining} 天）`
                          : `Expires: ${formatExpiry(expireAtStr)} (${daysRemaining} days left)`}</div>
                      )}
                      <div className="text-white/60">{language === 'zh'
                        ? `月度配额：无限`
                        : `Monthly Quota: Unlimited`}</div>
                      {purchasedMinutes > 0 && (
                        <div className="text-white/60">{language === 'zh'
                          ? `购买时长：${formatQuota(purchasedMinutes)}`
                          : `Purchased: ${formatQuota(purchasedMinutes)}`}</div>
                      )}
                    </div>
                  </div>
                )}
              </span>
            ) : isPro ? (
              <span className="text-prestige-500 drop-shadow-sm relative cursor-default"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                Pro <span className="italic font-bold ml-0.5 whitespace-nowrap opacity-80">Plan</span>
                {showTooltip && (
                  <div className="absolute left-0 bottom-full mb-2 z-[9999]">
                    <div className="bg-prestige-900 text-white text-[11px] rounded-lg px-3 py-2.5 shadow-xl whitespace-nowrap space-y-1">
                      {expireAtStr && (
                        <div>{language === 'zh'
                          ? `到期：${formatExpiry(expireAtStr)}（剩余 ${daysRemaining} 天）`
                          : `Expires: ${formatExpiry(expireAtStr)} (${daysRemaining} days left)`}</div>
                      )}
                      <div className="text-white/60">{language === 'zh'
                        ? `月度配额：${formatQuota(monthlyRemaining)}/${formatQuota(monthlyCap)}`
                        : `Monthly Quota: ${formatQuota(monthlyRemaining)}/${formatQuota(monthlyCap)}`}</div>
                      {purchasedMinutes > 0 && (
                        <div className="text-white/60">{language === 'zh'
                          ? `购买时长：${formatQuota(purchasedMinutes)}`
                          : `Purchased: ${formatQuota(purchasedMinutes)}`}</div>
                      )}
                    </div>
                  </div>
                )}
              </span>
            ) : (
              <div className="text-brand-900">
                <span>Free</span>
                <span className="italic text-ink-muted font-bold ml-1">Plan</span>
              </div>
            )}
            {isCofounder && (
              <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gradient-to-r from-amber-100 to-yellow-50 border border-amber-200/60 shadow-sm">
                <Shield size={10} className="text-amber-600" fill="currentColor" />
                <span className="text-[9px] font-black uppercase tracking-wider text-amber-700">Co-founder</span>
              </span>
            )}
          </div>

          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center relative overflow-hidden ${
              isPaid
                ? 'bg-prestige-500 text-white shadow-md'
                : 'bg-white text-ink-muted border border-surface-border'
            }`}
          >
            <Clock size={12} />
          </div>
        </div>

        {/* 时长信息 */}
        <div className="space-y-2">
          {/* 月度配额 */}
          <div className="space-y-1">
            <div
              className={`text-xs font-medium flex justify-between tracking-wide ${
                isPaid ? 'text-prestige-700' : 'text-ink-secondary'
              }`}
            >
              <span className="uppercase flex items-center gap-1">
                <Clock size={10} className={isPaid ? 'text-prestige-500' : 'text-ink-muted'} />
                {language === 'zh' ? '月度配额' : 'Monthly Quota'}
              </span>
              {isTeam ? (
                <span className="text-prestige-300 font-bold">
                  {language === 'zh' ? '无限' : 'Unlimited'}
                </span>
              ) : (
                <span className={isPaid ? 'text-prestige-500 font-mono' : 'font-mono'}>
                  {formatQuota(monthlyRemaining)}/{formatQuota(monthlyCap)}
                </span>
              )}
            </div>

            {!isTeam && (
              <div
                className={`h-1.5 w-full rounded-full overflow-hidden border relative ${
                  isPaid
                    ? 'bg-prestige-200/60 border-prestige-border/20'
                    : 'bg-surface-border/60 border-surface-border'
                }`}
              >
                <div
                  className={`h-full rounded-full transition-all duration-1000 ease-out relative ${
                    isPaid
                      ? 'bg-gradient-to-r from-prestige-500 to-prestige-accent'
                      : 'bg-gradient-to-r from-brand-500 to-brand-400'
                  }`}
                  style={{ width: `${usagePercent}%` }}
                >
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 购买时长 */}
          {purchasedMinutes > 0 && (
            <div className={`text-xs font-medium flex justify-between tracking-wide ${
              isPaid ? 'text-prestige-700' : 'text-ink-secondary'
            }`}>
              <span className="uppercase flex items-center gap-1">
                <Coins size={10} className={isPaid ? 'text-prestige-500' : 'text-ink-muted'} />
                {language === 'zh' ? '购买时长' : 'Purchased'}
              </span>
              <span className={isPaid ? 'text-prestige-500 font-mono' : 'font-mono'}>
                {formatQuota(purchasedMinutes)}
              </span>
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        <button
          onClick={isPaid ? onViewPlan : onUpgrade}
          className={`w-full mt-4 py-2 rounded-xl text-sm font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center gap-2 group/btn ${
            isPaid
              ? 'bg-white hover:bg-prestige-50 text-prestige-500 border border-prestige-border shadow-sm'
              : 'bg-white hover:bg-surface text-brand-900 border border-surface-border'
          }`}
        >
          <span>
            {isPaid
              ? language === 'zh'
                ? '查看详情'
                : 'View Plan'
              : language === 'zh'
              ? '升级会员'
              : 'Upgrade'}
          </span>
          <ArrowRight
            size={11}
            className={`transition-all duration-300 ${
              isPaid ? 'opacity-80 group-hover/btn:translate-x-1' : 'opacity-60'
            }`}
          />
        </button>
      </div>
    </div>
  );
}
